import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Form,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";

import {
  CreateGovernorateRequest,
} from "../../interfaces";
import { ActivityButton } from "../../components/Common";
import {
  useGetGovernorateQuery,
  useAddGovernorateMutation,
  useUpdateGovernorateMutation,
} from "../../store/api/governorateApi";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditGovernorateModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateGovernorateRequest>();

  const { data: governorate, error: getGovernorateError } =
    useGetGovernorateQuery(id as string, { skip: !id });
  const [addGovernorate, { isLoading: isAdding }] = useAddGovernorateMutation();
  const [updateGovernorate, { isLoading: isUpdating }] =
    useUpdateGovernorateMutation();

  const [submitError, setSubmitError] = useState<string | null>(null);

  //#region form
  const form = useFormik<CreateGovernorateRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
    },
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            })
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            })
          ),
      }),
    }),
    onSubmit: async (values) => {
      try {
        setSubmitError(null);
        if (id) {
          await updateGovernorate({ id, data: values }).unwrap();
        } else {
          await addGovernorate(values).unwrap();
        }
        onClose();
      } catch (err: any) {
        setSubmitError(err?.data?.message || "An error occurred");
      }
    },
  });
  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    setSubmitError(null);
    onCloseClick(!show);
  };

  useEffect(() => {
    if (id && governorate) {
      form.setValues({ name: governorate.name });
    }
  }, [governorate]);

  //#endregion

  return (
    <Modal
      id="addEdit"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {id
            ? t("editItem", { item: "governorate" })
            : t("addItem", { item: "governorate" })}
        </ModalHeader>
        {typeof submitError === "string" && (
          <Alert color="danger" className="rounded-0 mb-0">
            <p className="mb-0">{submitError}</p>
          </Alert>
        )}
        {typeof getGovernorateError === "string" && (
          <Alert color="danger" className="rounded-0 mb-0">
            <p className="mb-0">{getGovernorateError}</p>
          </Alert>
        )}
        <ModalBody>
          <div className="mb-3">
            <label htmlFor="name.en" className="form-label">
              {t("nameEn")}
              <span className="text-danger">*</span>
            </label>
            <Input
              disabled={!!getGovernorateError}
              autoComplete="off"
              id="name.en"
              name="name.en"
              value={form.values?.name.en}
              type="text"
              className="form-control"
              placeholder={t("enterInEnglish")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.en && form.errors.name?.en ? true : false
              }
            />
            {form.touched.name?.en && form.errors.name?.en ? (
              <FormFeedback type="invalid">
                {form.errors.name?.en as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label htmlFor="name.ar" className="form-label">
              {t("nameAr")}
              <span className="text-danger">*</span>
            </label>
            <Input
              style={{ fontFamily: "aljannat", direction: "rtl" }}
              disabled={!!getGovernorateError}
              autoComplete="off"
              auto
              id="name.ar"
              name="name.ar"
              value={form.values.name?.ar}
              type="text"
              className="form-control"
              placeholder={t("enterInArabic")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.ar && form.errors.name?.ar ? true : false
              }
            />
            {form.touched.name?.ar && form.errors.name?.ar ? (
              <FormFeedback type="invalid">
                {form.errors.name?.ar as string}
              </FormFeedback>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={isAdding || isUpdating}
            disabled={
              isAdding || isUpdating || !!getGovernorateError
            }
          ></ActivityButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
